import { useState } from 'react'
import { Search, AlertTriangle, Info, ChevronDown, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import ExportButtons from '../../components/ExportButtons'
import { localizeApiDetail } from '../../i18n/apiError'
import { formatNorwegianDate } from '../../utils/dateUtils'
import KvoteBadge from './KvoteBadge'
import KjoretoyDetaljer from './KjoretoyDetaljer'
import {
  parseSokeliste,
  tekniskOppslag,
  type Kvote,
  type KjoretoyRad,
  type Feil,
} from './oppslagApi'
import type { OppslagKey } from './detaljFelter'

const MAKS_BATCH = 100

/** dd.MM.yyyy, as everywhere else in the app. Shared by the table and the
 *  export, so a date cannot render one way on screen and another in the file. */
const dato = (v: unknown) => (v ? formatNorwegianDate(String(v)) : '—')

const KOLONNER: { key: string; labelKey: OppslagKey; format?: (v: unknown) => string }[] = [
  { key: 'kjennemerke', labelKey: 'kolonner.kjennemerke' },
  { key: 'merke', labelKey: 'kolonner.merke' },
  { key: 'handelsbetegnelse', labelKey: 'kolonner.modell' },
  { key: 'registrert_forstegang', labelKey: 'kolonner.regForsteGang', format: dato },
  { key: 'pkk_sist_godkjent', labelKey: 'kolonner.sistEuGodkjent', format: dato },
  { key: 'pkk_kontrollfrist', labelKey: 'kolonner.fristEuKontroll', format: dato },
  { key: 'registreringsstatus_tekst', labelKey: 'kolonner.status' },
  { key: 'drivstoff', labelKey: 'kolonner.drivstoff' },
  { key: 'understellsnummer', labelKey: 'kolonner.understellsnummer' },
]

/** Norwegian feilkoder rendered for humans. Unknown codes fall through as-is
 *  rather than being swallowed — an unexplained code is still information. */
const FEILTEKST: Record<string, OppslagKey> = {
  IKKE_FUNNET: 'feilkoder.ikkeFunnet',
  TOMT_SOK: 'feilkoder.tomtSok',
  FOR_MANGE_FORESPORSLER: 'feilkoder.forMangeForesporsler',
  UGYLDIG_FORMAT_UNR_ELLER_KJENNEMERKE: 'feilkoder.ugyldigFormat',
}

export default function TekniskTab() {
  const { t } = useTranslation('oppslag')
  const [raa, setRaa] = useState('')
  const [laster, setLaster] = useState(false)
  const [rader, setRader] = useState<KjoretoyRad[]>([])
  const [feil, setFeil] = useState<Feil[]>([])
  const [kvote, setKvote] = useState<Kvote | null>(null)
  const [apiFeil, setApiFeil] = useState<string | null>(null)
  /** Indices of rows whose detail panel is open. A Set rather than a single
   *  index so two vehicles can be compared side by side. */
  const [apne, setApne] = useState<Set<number>>(new Set())

  const sok = parseSokeliste(raa)
  const forMange = sok.length > MAKS_BATCH

  function veksle(index: number) {
    setApne((forrige) => {
      const neste = new Set(forrige)
      if (neste.has(index)) neste.delete(index)
      else neste.add(index)
      return neste
    })
  }

  async function utfor() {
    if (!sok.length || forMange) return
    setLaster(true)
    setApiFeil(null)
    try {
      const svar = await tekniskOppslag(sok)
      setRader(svar.treff)
      setFeil(svar.feil)
      setKvote(svar.kvote)
      // A single hit means the user asked about one car, so the detail they
      // came for is already open. More than one is a list to scan first.
      setApne(svar.treff.length === 1 ? new Set([0]) : new Set())
    } catch (e: any) {
      const detalj = e?.response?.data?.detail
      setApiFeil(
        typeof detalj === 'string'
          ? (localizeApiDetail(detalj) ?? t('felles.lookupFailed'))
          : (localizeApiDetail(detalj?.melding) ?? t('felles.lookupFailed')),
      )
    } finally {
      setLaster(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="card p-4 space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-semibold text-gray-900 dark:text-white">
              {t('teknisk.title')}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('teknisk.intro', { max: MAKS_BATCH })}
            </p>
          </div>
          <KvoteBadge kvote={kvote} api="teknisk" />
        </div>

        <textarea
          data-testid="teknisk-input"
          value={raa}
          onChange={(e) => setRaa(e.target.value)}
          rows={5}
          placeholder={'EE16703\nEB11111\nWBY11CF01NCK45971'}
          className="w-full rounded-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 p-2 text-sm font-mono"
        />

        <div className="flex items-center gap-3">
          <button
            data-testid="teknisk-sok"
            onClick={utfor}
            disabled={laster || !sok.length || forMange}
            className="btn-primary text-sm disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Search className="w-4 h-4" />
            {laster ? t('felles.searching') : t('felles.searchCount', { n: sok.length })}
          </button>
          {forMange && (
            <span
              data-testid="teknisk-formange"
              className="text-sm text-red-600 dark:text-red-400"
            >
              {t('felles.tooMany', { n: sok.length, max: MAKS_BATCH })}
            </span>
          )}
          {!!sok.length && !forMange && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {t('teknisk.cost', { n: sok.length })}
            </span>
          )}
        </div>
      </div>

      {apiFeil && (
        <div
          data-testid="teknisk-apifeil"
          className="card p-3 flex items-start gap-2 text-sm text-red-700 dark:text-red-300"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{apiFeil}</span>
        </div>
      )}

      {!!rader.length && (
        <div className="card p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600 dark:text-gray-300">
              {t('teknisk.hits', { n: rader.length })}
            </span>
            {/* The table export: one row per vehicle, the visible columns.
                Each detail panel carries its own, shaped as a spec sheet. */}
            <div data-testid="teknisk-eksport">
              <ExportButtons
                data={rader}
                columns={KOLONNER.map(({ labelKey, ...k }) => ({ ...k, label: t(labelKey) }))}
                metadata={{
                  title: t('teknisk.exportTitle'),
                  filters: [t('teknisk.exportFilter', { n: sok.length })],
                }}
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="data-table data-table-compact" data-testid="teknisk-tabell">
              <thead>
                <tr>
                  {KOLONNER.map((k) => (
                    <th key={k.key}>{t(k.labelKey)}</th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {rader.map((r, i) => {
                  const apen = apne.has(i)
                  return [
                    <tr key={`${r.kjennemerke}-${i}`}>
                      <td>
                        {r.kjennemerke}
                        {/* SVV resolves ANY plate the vehicle has held, so a
                            search can land on a different plate than the one
                            typed. Saying so is the whole point. */}
                        {r.matchet_pa && (
                          <span
                            className="ml-1 inline-flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300"
                            title={t('teknisk.matchedTitle', { sokt: r.sokt })}
                          >
                            <Info className="w-3 h-3" />
                            {t('teknisk.matched', { plate: r.matchet_pa })}
                          </span>
                        )}
                      </td>
                      {KOLONNER.slice(1).map((k) => {
                        const verdi = (r as any)[k.key]
                        return (
                          <td key={k.key}>
                            {k.format ? k.format(verdi) : (verdi ?? '—')}
                          </td>
                        )
                      })}
                      <td>
                        {r.detaljer && (
                          <button
                            data-testid={`teknisk-detaljer-knapp-${i}`}
                            onClick={() => veksle(i)}
                            aria-expanded={apen}
                            className="inline-flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline whitespace-nowrap"
                          >
                            {apen ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                            {apen ? t('teknisk.hideDetails') : t('teknisk.showDetails')}
                          </button>
                        )}
                      </td>
                    </tr>,
                    apen && r.detaljer ? (
                      <tr key={`${r.kjennemerke}-${i}-detaljer`}>
                        <td colSpan={KOLONNER.length + 1} className="p-0">
                          <KjoretoyDetaljer
                            detaljer={r.detaljer}
                            kjennemerke={r.kjennemerke}
                            beskrivelse={[r.merke, r.handelsbetegnelse]
                              .filter(Boolean)
                              .join(' ')}
                          />
                        </td>
                      </tr>
                    ) : null,
                  ]
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!!feil.length && (
        <div className="card p-4" data-testid="teknisk-feil">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
            {t('teknisk.notFound', { n: feil.length })}
          </h3>
          <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
            {feil.map((f, i) => (
              <li key={i}>
                <span className="font-mono">{f.sokt || t('felles.empty')}</span> —{' '}
                {FEILTEKST[f.kode] ? t(FEILTEKST[f.kode]) : f.kode}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
